import { api } from "@/lib/apiClient";
import type { Instance, DockerContainer, RebuildResult, CreateContainerResult } from "@/server/types";
import Link from "next/link";
import { useEffect, useMemo, useState, useCallback } from "react";

// Connection timeout: 60 seconds (3 missed heartbeats at 20s interval)
const DISCONNECT_THRESHOLD = 60000;

type InstanceStatus = Instance["status"];

function isConnected(instance: Instance): boolean {
  return Date.now() - instance.lastSeen < DISCONNECT_THRESHOLD;
}

function getStatusColor(status: InstanceStatus, connected: boolean): string {
  if (!connected) return "bg-zinc-400";
  switch (status) {
    case "running":
      return "bg-green-500";
    case "idle":
      return "bg-yellow-500";
    case "error":
      return "bg-red-500";
  }
}

function getTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Truncate text with ellipsis
const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + "…";
};

// Extract project name from path (last segment)
const getProjectDisplayName = (workingDir: string | null, fallbackName: string): string => {
  if (!workingDir) return fallbackName;
  const segments = workingDir.split("/").filter(Boolean);
  return segments[segments.length - 1] || fallbackName;
};

function InstanceRow({ instance, dimmed = false }: { instance: Instance; dimmed?: boolean }) {
  const connected = isConnected(instance);

  return (
    <Link
      href={`/instance/${instance.id}`}
      className={`block p-3 rounded-md transition-colors ${
        dimmed
          ? "bg-zinc-100/50 dark:bg-zinc-800/30 hover:bg-zinc-100 dark:hover:bg-zinc-800/50"
          : "bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(instance.status, connected)}`}
          />
          <span className={`text-sm font-mono ${dimmed ? "text-zinc-400 dark:text-zinc-500" : "text-zinc-600 dark:text-zinc-400"}`}>
            {instance.id}
          </span>
          {instance.branchName && (
            <>
              <span className="text-zinc-300 dark:text-zinc-600">·</span>
              <span className={`text-xs ${dimmed ? "text-zinc-400" : "text-blue-600 dark:text-blue-400"}`}>
                {instance.branchName}
              </span>
            </>
          )}
          {instance.worktreePath && (
            <span className="text-xs text-zinc-400" title="Isolated worktree">
              🌲
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <span className={`text-xs capitalize ${dimmed ? "text-zinc-400" : "text-zinc-500"}`}>
            {connected ? instance.status : "disconnected"}
          </span>
          <span className={`text-xs ${dimmed ? "text-zinc-400" : "text-zinc-400"}`}>
            {getTimeAgo(instance.lastSeen)}
          </span>
        </div>
      </div>
      {instance.taskSummary && (
        <div className={`mt-1.5 ml-5 text-xs ${dimmed ? "text-zinc-400" : "text-zinc-500 dark:text-zinc-500"}`}>
          {truncate(instance.taskSummary, 80)}
        </div>
      )}
    </Link>
  );
}

// Number of recent sessions to show before collapsing into "older"
const RECENT_SESSION_COUNT = 3;

interface ProjectGroup {
  workingDir: string;      // Group key (path or "unknown")
  displayName: string;     // Human-readable name
  instances: Instance[];
  recentInstances: Instance[];
  archivedInstances: Instance[];
  hasRunning: boolean;
  hasConnected: boolean;
  latestSeen: number;
  activeBranch: string | null;
}

function ProjectCard({ group, dimmed = false }: { group: ProjectGroup; dimmed?: boolean }) {
  const [expanded, setExpanded] = useState(!dimmed);
  const [showArchived, setShowArchived] = useState(false);

  const activeCount = group.instances.filter(i => isConnected(i)).length;
  const archivedCount = group.archivedInstances.length;

  return (
    <div className={`rounded-lg border overflow-hidden ${
      dimmed
        ? "bg-zinc-50 dark:bg-zinc-900/50 border-zinc-200/50 dark:border-zinc-800/50"
        : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800"
    }`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                group.hasRunning ? "bg-green-500" : group.hasConnected ? "bg-yellow-500" : "bg-zinc-400"
              }`}
            />
            <h2 className={`font-medium truncate ${dimmed ? "text-zinc-500 dark:text-zinc-400" : "text-zinc-900 dark:text-zinc-100"}`}>
              {group.displayName}
            </h2>
            <span className="text-xs text-zinc-500 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded-full flex-shrink-0">
              {activeCount > 0 ? `${activeCount} active` : `${group.instances.length} session${group.instances.length !== 1 ? "s" : ""}`}
            </span>
          </div>
          {group.workingDir !== "unknown" && (
            <div className={`mt-1 ml-5 text-xs truncate ${dimmed ? "text-zinc-400" : "text-zinc-400 dark:text-zinc-500"}`}>
              {group.workingDir}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
          {group.activeBranch && (
            <span className="text-xs text-blue-600 dark:text-blue-400 hidden sm:inline">
              {group.activeBranch}
            </span>
          )}
          <span className="text-xs text-zinc-400">
            {getTimeAgo(group.latestSeen)}
          </span>
          <svg
            className={`w-4 h-4 text-zinc-400 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          {/* Recent sessions */}
          {group.recentInstances.map((instance) => (
            <InstanceRow key={instance.id} instance={instance} dimmed={dimmed} />
          ))}

          {/* Archived sessions toggle */}
          {archivedCount > 0 && (
            <div className="pt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowArchived(!showArchived);
                }}
                className="flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
              >
                <svg
                  className={`w-3 h-3 transition-transform ${showArchived ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Older sessions ({archivedCount})
              </button>

              {showArchived && (
                <div className="mt-2 space-y-2">
                  {group.archivedInstances.map((instance) => (
                    <InstanceRow key={instance.id} instance={instance} dimmed />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDisconnected, setShowDisconnected] = useState(false);

  // Docker management state
  const [dockerAvailable, setDockerAvailable] = useState<boolean | null>(null);
  const [dockerContainers, setDockerContainers] = useState<DockerContainer[]>([]);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<RebuildResult | null>(null);

  // Create container modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    workingDir: "",
    instanceName: "",
    task: "You are an expert developer",
    network: "traefik-public",
  });
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState<CreateContainerResult | null>(null);

  // Group instances by hostPath (original machine path), then split into connected/disconnected
  const { connectedGroups, disconnectedGroups } = useMemo(() => {
    const groups = new Map<string, Instance[]>();

    for (const instance of instances) {
      // Group by hostPath (original machine path), fallback to workingDir, then "unknown"
      const groupKey = instance.hostPath || instance.workingDir || "unknown";
      const existing = groups.get(groupKey) || [];
      existing.push(instance);
      groups.set(groupKey, existing);
    }

    const allGroups: ProjectGroup[] = Array.from(groups.entries())
      .map(([workingDir, groupInstances]) => {
        // Sort by lastSeen descending
        const sorted = groupInstances.sort((a, b) => b.lastSeen - a.lastSeen);

        // Split into recent and archived
        const recentInstances = sorted.slice(0, RECENT_SESSION_COUNT);
        const archivedInstances = sorted.slice(RECENT_SESSION_COUNT);

        // Find the active branch (from running or most recent connected instance)
        const runningInstance = sorted.find(i => i.status === "running" && isConnected(i));
        const connectedInstance = sorted.find(i => isConnected(i));
        const activeBranch = runningInstance?.branchName || connectedInstance?.branchName || null;

        // Get display name from path or fallback to first instance name
        const displayName = workingDir === "unknown"
          ? "Unknown Project"
          : getProjectDisplayName(workingDir, sorted[0]?.name || "Unknown");

        return {
          workingDir,
          displayName,
          instances: sorted,
          recentInstances,
          archivedInstances,
          hasRunning: groupInstances.some((i) => i.status === "running" && isConnected(i)),
          hasConnected: groupInstances.some((i) => isConnected(i)),
          latestSeen: Math.max(...groupInstances.map((i) => i.lastSeen)),
          activeBranch,
        };
      })
      .sort((a, b) => b.latestSeen - a.latestSeen);

    // Split into connected and disconnected
    const connected = allGroups.filter((g) => g.hasConnected);
    const disconnected = allGroups.filter((g) => !g.hasConnected);

    return { connectedGroups: connected, disconnectedGroups: disconnected };
  }, [instances]);

  async function fetchInstances() {
    try {
      const data = await api.listInstances();
      setInstances(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch instances");
    } finally {
      setLoading(false);
    }
  }

  // Check Docker availability and list containers
  const checkDocker = useCallback(async () => {
    try {
      const status = await api.checkDockerStatus();
      console.log("Docker status:", status);
      setDockerAvailable(status.available);
      if (status.available) {
        const containers = await api.listDockerContainers();
        console.log("Docker containers:", containers);
        setDockerContainers(containers);
      }
    } catch (err) {
      console.error("Docker check failed:", err);
      setDockerAvailable(false);
    }
  }, []);

  // Rebuild and restart all containers
  const handleRebuildAll = useCallback(async () => {
    if (rebuilding) return;

    const confirmed = window.confirm(
      `This will rebuild the claude-agent image and restart ${dockerContainers.length} container(s). Continue?`
    );
    if (!confirmed) return;

    setRebuilding(true);
    setRebuildResult(null);

    try {
      const result = await api.rebuildAndRestartAll();
      setRebuildResult(result);
      // Refresh container list after rebuild
      await checkDocker();
    } catch (err) {
      setRebuildResult({
        success: false,
        message: err instanceof Error ? err.message : "Failed to rebuild",
        containersRestarted: 0,
        errors: [err instanceof Error ? err.message : "Unknown error"],
      });
    } finally {
      setRebuilding(false);
    }
  }, [rebuilding, dockerContainers.length, checkDocker]);

  // Create new container
  const handleCreateContainer = useCallback(async () => {
    if (creating || !createForm.workingDir.trim() || !createForm.task.trim()) return;

    setCreating(true);
    setCreateResult(null);

    try {
      const result = await api.createContainer({
        workingDir: createForm.workingDir.trim(),
        instanceName: createForm.instanceName.trim() || undefined,
        task: createForm.task.trim(),
        network: createForm.network.trim() || undefined,
      });
      setCreateResult(result);
      if (result.success) {
        // Refresh lists after creation
        await checkDocker();
        await fetchInstances();
        // Close modal after short delay to show success
        setTimeout(() => {
          setShowCreateModal(false);
          setCreateResult(null);
          setCreateForm({
            workingDir: "",
            instanceName: "",
            task: "You are an expert developer",
            network: "traefik-public",
          });
        }, 1500);
      }
    } catch (err) {
      setCreateResult({
        success: false,
        message: err instanceof Error ? err.message : "Failed to create container",
      });
    } finally {
      setCreating(false);
    }
  }, [creating, createForm, checkDocker]);

  useEffect(() => {
    fetchInstances();
    checkDocker();

    // Refresh every 5 seconds
    const interval = setInterval(fetchInstances, 5000);
    // Check Docker less frequently
    const dockerInterval = setInterval(checkDocker, 30000);
    return () => {
      clearInterval(interval);
      clearInterval(dockerInterval);
    };
  }, [checkDocker]);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <header className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
            Claude Manager
          </h1>

          {/* Docker buttons */}
          {dockerAvailable && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors bg-green-600 hover:bg-green-700 text-white"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Container
              </button>
              <button
                onClick={handleRebuildAll}
                disabled={rebuilding}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${
                  rebuilding
                    ? "bg-zinc-200 dark:bg-zinc-700 text-zinc-500 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {rebuilding ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Rebuilding...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Rebuild & Restart ({dockerContainers.length})
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Rebuild result banner */}
        {rebuildResult && (
          <div
            className={`px-4 py-2 text-sm ${
              rebuildResult.success
                ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200"
            }`}
          >
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <span>{rebuildResult.message}</span>
              <button
                onClick={() => setRebuildResult(null)}
                className="text-current opacity-60 hover:opacity-100"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {rebuildResult.errors.length > 0 && (
              <div className="max-w-4xl mx-auto mt-1 text-xs opacity-80">
                {rebuildResult.errors.map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
            Projects ({connectedGroups.length})
          </h2>
          <span className="text-sm text-zinc-500">
            {instances.length} total session{instances.length !== 1 ? "s" : ""}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading...</div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">{error}</div>
        ) : connectedGroups.length === 0 && disconnectedGroups.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            No instances running. Start a local agent to see it here.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Connected projects */}
            {connectedGroups.length === 0 && disconnectedGroups.length > 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No connected instances. Check the disconnected section below.
              </div>
            ) : (
              connectedGroups.map((group) => (
                <ProjectCard key={group.workingDir} group={group} />
              ))
            )}

            {/* Disconnected projects section */}
            {disconnectedGroups.length > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setShowDisconnected(!showDisconnected)}
                  className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors mb-4"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${showDisconnected ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Disconnected ({disconnectedGroups.length} project{disconnectedGroups.length !== 1 ? "s" : ""})
                </button>

                {showDisconnected && (
                  <div className="space-y-3">
                    {disconnectedGroups.map((group) => (
                      <ProjectCard key={group.workingDir} group={group} dimmed />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create Container Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                New Container
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateResult(null);
                }}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Working Directory <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={createForm.workingDir}
                  onChange={(e) => setCreateForm({ ...createForm, workingDir: e.target.value })}
                  placeholder="/path/to/project"
                  className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Instance Name <span className="text-zinc-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={createForm.instanceName}
                  onChange={(e) => setCreateForm({ ...createForm, instanceName: e.target.value })}
                  placeholder="my-project"
                  className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Docker Network <span className="text-zinc-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={createForm.network}
                  onChange={(e) => setCreateForm({ ...createForm, network: e.target.value })}
                  placeholder="traefik-public"
                  className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Initial Task / System Prompt <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={createForm.task}
                  onChange={(e) => setCreateForm({ ...createForm, task: e.target.value })}
                  placeholder="You are an expert developer"
                  rows={3}
                  className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-md text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Result banner */}
              {createResult && (
                <div
                  className={`p-3 rounded-md text-sm ${
                    createResult.success
                      ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                      : "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200"
                  }`}
                >
                  {createResult.message}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200 dark:border-zinc-800">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateResult(null);
                }}
                className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateContainer}
                disabled={creating || !createForm.workingDir.trim() || !createForm.task.trim()}
                className={`px-4 py-2 text-sm rounded-md transition-colors ${
                  creating || !createForm.workingDir.trim() || !createForm.task.trim()
                    ? "bg-zinc-200 dark:bg-zinc-700 text-zinc-500 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                {creating ? "Creating..." : "Create Container"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
